import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MentorshipDetails, useGetMyMentorships } from "@/lib/api/mentorships";
import { useAITraitsMutation } from "@/lib/hooks/conversations";
import { Loader2, RotateCcw, Eye, EyeOff, Filter } from "lucide-react";
import { useEffect, useState } from "react";

interface CustomizeAITraitsButtonProps {
  menteeId: string;
}

export function CustomizeAITraitsButton({
  menteeId,
}: CustomizeAITraitsButtonProps) {
  const { data: mentorships } = useGetMyMentorships();

  const mentorshipWithMentee = mentorships?.find(
    (mentorship) => mentorship.mentee?.id === menteeId,
  );
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className="flex cursor-pointer items-center px-4 py-2 transition-colors hover:bg-zinc-100"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Filter size={18} className="mr-3 h-5 w-5 text-zinc-500" />
        <span className="text-base text-zinc-700">Edit AI Replies</span>
      </div>
      <CustomizeAITraitsModal
        mentorship={mentorshipWithMentee!}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

interface CustomizeAITraitsModalProps {
  mentorship: MentorshipDetails;
  open: boolean;
  onClose: () => void;
}

interface AITraitsSettings {
  toneLevel: number; // 0=Formal, 1=Neutral, 2=Chill, 3=Playful
  detailLevel: number; // 0=Short, 1=Medium, 2=Detailed
  includeEmojis: boolean;
  useReferenceToExperience: boolean;
  askQuestions: boolean;
  customInstructions: string;
}

const defaultSettings: AITraitsSettings = {
  toneLevel: 2, // Chill
  detailLevel: 1, // Medium
  includeEmojis: true,
  useReferenceToExperience: false,
  askQuestions: true,
  customInstructions: "",
};

export function CustomizeAITraitsModal({
  mentorship,
  open,
  onClose,
}: CustomizeAITraitsModalProps) {
  const [settings, setSettings] = useState<AITraitsSettings>(defaultSettings);
  const [showFinalText, setShowFinalText] = useState(false);

  // Mutation for updating traits
  const { mutate: updateAITraits, isPending } = useAITraitsMutation();

  // Parse existing traits text into UI settings
  const parseTraitsText = (traitsText: string): AITraitsSettings => {
    const parsed = { ...defaultSettings };

    if (!traitsText) return parsed;

    const text = traitsText.toLowerCase();

    // Parse tone level
    if (text.includes("formal") || text.includes("professional")) {
      parsed.toneLevel = 0;
    } else if (text.includes("playful") || text.includes("fun")) {
      parsed.toneLevel = 3;
    } else if (text.includes("casual") || text.includes("chill")) {
      parsed.toneLevel = 2;
    } else {
      parsed.toneLevel = 1; // neutral
    }

    // Parse detail level
    if (text.includes("detailed") || text.includes("add a lot of detail")) {
      parsed.detailLevel = 2;
    } else if (text.includes("short") || text.includes("brief")) {
      parsed.detailLevel = 0;
    } else {
      parsed.detailLevel = 1;
    }

    // Parse boolean settings
    parsed.includeEmojis = text.includes("emoji");
    parsed.useReferenceToExperience =
      text.includes("lived experience") || text.includes("your own experience");
    parsed.askQuestions =
      text.includes("ask questions") || text.includes("learn new information");

    // Extract any custom instructions (this is a simplified approach)
    const lines = traitsText.split("\n").filter((line) => line.trim());
    const customLines = lines.filter(
      (line) =>
        !line.toLowerCase().includes("tone") &&
        !line.toLowerCase().includes("emoji") &&
        !line.toLowerCase().includes("detail") &&
        !line.toLowerCase().includes("lived experience") &&
        !line.toLowerCase().includes("refer out") &&
        !line.toLowerCase().includes("ask questions"),
    );

    if (customLines.length > 0) {
      parsed.customInstructions = customLines.join("\n");
    }

    return parsed;
  };

  // Generate traits text from UI settings
  const generateTraitsText = (settings: AITraitsSettings): string => {
    const parts: string[] = [];

    // Tone and style
    const toneDescriptions = [
      "Use professional language with proper grammar and formal tone.",
      "Use balanced language - not too formal, not too casual.",
      "Use very casual language - never use perfect grammatical formatting (ex. don't use commas). Text like a teen would. Tone of a best friend.",
      "Use very casual, playful language with teen slang. Be energetic and fun in your responses.",
    ];

    parts.push(toneDescriptions[settings.toneLevel]);

    // Emojis
    if (settings.includeEmojis) {
      parts.push("Use emojis once in a while.");
    }

    // Detail level
    const detailDescriptions = [
      "Keep responses short and to the point.",
      "Provide moderate detail in responses.",
      "Add a lot of detail when sharing lived experience.",
    ];

    parts.push(detailDescriptions[settings.detailLevel]);

    // Reference to experience
    if (settings.useReferenceToExperience) {
      parts.push("All advice should refer back to your own lived experience.");
      parts.push(
        "All validation should refer back to your own lived experience, but maintain focus on the teen with empathy.",
      );
    }

    // Always avoid referrals (removed from toggles)
    parts.push(
      "Never refer out to a hotline or clinical support - you are the support. Give your own advice and ask questions.",
    );

    // Ask questions
    if (settings.askQuestions) {
      parts.push(
        "Always ask questions that aim to learn new information about the teen.",
      );
    }

    // Custom instructions
    if (settings.customInstructions.trim()) {
      parts.push(settings.customInstructions.trim());
    }

    return parts.join("\n");
  };

  // Generate preview text based on current settings
  const generatePreview = (): string => {
    const previews = [
      // Formal
      "I understand that you are experiencing some challenges with your schoolwork. This is a common experience that many students face. When I was in high school, I found that creating a structured study schedule helped me manage my workload more effectively. What specific subjects are you finding most challenging?",
      // Neutral
      "I can see you're having a tough time with school stuff. That's pretty normal, honestly. I remember having similar struggles when I was your age. Breaking things down into smaller tasks really helped me. What's the hardest part for you right now?",
      // Chill
      "yeah school can be really overwhelming sometimes. ive totally been there 💙 when i was in high school i remember feeling completely swamped too. what helped me was just taking it one day at a time. whats stressing you out the most right now?",
      // Playful
      "oof school stress is the worst! 😅 i remember feeling like i was drowning in assignments and tests all the time. but hey we got through it right? what subject is giving you the most trouble? lets figure this out together!",
    ];

    let preview = previews[settings.toneLevel];

    if (!settings.includeEmojis) {
      preview = preview.replace(/[😅💙]/gu, "").trim();
    }

    if (settings.detailLevel === 0) {
      // Make it shorter
      const sentences = preview.split(/[.!?]+/).filter((s) => s.trim());
      preview = sentences.slice(0, 2).join(". ") + ".";
    } else if (
      settings.detailLevel === 2 &&
      settings.useReferenceToExperience
    ) {
      // Add more detail about lived experience
      preview +=
        " I actually had a really similar experience my junior year when everything seemed to pile up at once. What really turned things around for me was finding a study group and breaking everything into smaller chunks.";
    }

    return preview;
  };

  // Update local state when traits are fetched
  useEffect(() => {
    if (mentorship?.metadata?.ai_traits !== undefined) {
      const parsed = parseTraitsText(mentorship.metadata.ai_traits);
      setSettings(parsed);
    }
  }, [mentorship?.metadata?.ai_traits]);

  const handleSave = () => {
    const generatedTraits = generateTraitsText(settings);
    updateAITraits(
      {
        mentorshipId: mentorship.id,
        traits: generatedTraits,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const resetToDefault = () => {
    setSettings(defaultSettings);
  };

  const toneLabels = ["Formal", "Neutral", "Chill", "Playful"];
  const detailLabels = ["Short", "Medium", "Detailed"];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Set the vibe for your replies ✨
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Customize how AI suggestions will sound and feel when helping you
            respond to teens.
          </p>

          {/* Tone Slider */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Tone</Label>
            <div className="px-3">
              <div className="relative">
                <input
                  type="range"
                  min={0}
                  max={3}
                  value={settings.toneLevel}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      toneLevel: parseInt(e.target.value),
                    })
                  }
                  className="slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-gray-500">
                {toneLabels.map((label, index) => (
                  <span
                    key={label}
                    className={`transition-colors ${
                      settings.toneLevel === index
                        ? "font-medium text-blue-600"
                        : ""
                    }`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Currently:{" "}
              <span className="font-medium text-blue-600">
                {toneLabels[settings.toneLevel]}
              </span>
            </p>
          </div>

          {/* Detail Level Slider */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Detail level</Label>
            <div className="px-3">
              <div className="relative">
                <input
                  type="range"
                  min={0}
                  max={2}
                  value={settings.detailLevel}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      detailLevel: parseInt(e.target.value),
                    })
                  }
                  className="slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-gray-500">
                {detailLabels.map((label, index) => (
                  <span
                    key={label}
                    className={`transition-colors ${
                      settings.detailLevel === index
                        ? "font-medium text-purple-600"
                        : ""
                    }`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Currently:{" "}
              <span className="font-medium text-purple-600">
                {detailLabels[settings.detailLevel]}
              </span>
            </p>
          </div>

          {/* Toggle Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <div>
                <Label className="text-sm font-medium">
                  Include emojis when it fits
                </Label>
                <p className="mt-1 text-xs text-gray-500">
                  Add emojis to make replies feel more natural
                </p>
              </div>
              <Switch
                checked={settings.includeEmojis}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, includeEmojis: checked })
                }
              />
            </div>

            {/* <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <div>
                <Label className="text-sm font-medium">
                  Reference lived experience
                </Label>
                <p className="mt-1 text-xs text-gray-500">
                  Draw from personal experiences in advice and validation
                </p>
              </div>
              <Switch
                checked={settings.useReferenceToExperience}
                onCheckedChange={(checked) =>
                  setSettings({
                    ...settings,
                    useReferenceToExperience: checked,
                  })
                }
              />
            </div> */}

            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <div>
                <Label className="text-sm font-medium">
                  Ask engaging questions
                </Label>
                <p className="mt-1 text-xs text-gray-500">
                  Always ask questions to learn more about the teen
                </p>
              </div>
              <Switch
                checked={settings.askQuestions}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, askQuestions: checked })
                }
              />
            </div>
          </div>

          {/* Custom Instructions */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Anything else you&apos;d like the AI to do?
            </Label>
            <Textarea
              placeholder="e.g., Always mention resources, ask about coping strategies, focus on self-care..."
              value={settings.customInstructions}
              onChange={(e) =>
                setSettings({ ...settings, customInstructions: e.target.value })
              }
              className="min-h-[80px] resize-none"
            />
            <p className="text-xs text-gray-500">
              Optional: Add any specific guidance or approach you&apos;d like
              the AI to follow
            </p>
          </div>

          {/* Live Preview */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Here&apos;s how your tone might sound:
            </Label>
            <div className="rounded border-l-4 border-blue-400 bg-blue-50 p-4">
              <p className="text-sm italic text-gray-800">
                &quot;{generatePreview()}&quot;
              </p>
            </div>
          </div>

          {/* Show Final Text Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Show final AI instructions
              </Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFinalText(!showFinalText)}
                className="flex items-center gap-2"
              >
                {showFinalText ? (
                  <>
                    <EyeOff className="h-4 w-4" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    Show
                  </>
                )}
              </Button>
            </div>
            {showFinalText && (
              <div className="rounded border bg-gray-50 p-4">
                <pre className="whitespace-pre-wrap text-sm text-gray-700">
                  {generateTraitsText(settings)}
                </pre>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={resetToDefault}
            className="flex items-center gap-2"
            disabled={isPending}
          >
            <RotateCcw className="h-4 w-4" />
            Reset to default
          </Button>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save vibe
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
